import React, { useState, useEffect } from 'react';
import '../styles/rtl-input.css'; // تحسينات RTL لحقول الإدخال
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Autocomplete,
  Card,
  Divider,
  ListItemText,
  Switch,
  Container,
  useTheme,
  useMediaQuery,
  Grid,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api';
import MapPicker from '../components/MapPicker';
import { 
  SurveyFormData, 
  SurveyParticipant, 
  Participant,
  Farmer,
  Department,
  Governorate,
  State,
  Village,
  Crop,
  Usage,
  SampleType,
  GeneticStatus,
  CollectionSource,
  Material,
  Topography,
  LocationStatus,
  SoilTexture,
  SoilColor,
  Hardness,
  Drainage,
  DiseasePest,
  SoilPH,
  WaterPH,
  SeedSurvey
} from '../types';

interface WithId {
  id: number;
  [key: string]: any; // للسماح بأي خصائص أخرى
}

interface ApiSurveyResponse {
  id: number;
  survey_number: string;
  farmer?: number | WithId;  // Added farmer field
  farmer_name: string;
  farmer_phone: string;
  participants?: SurveyParticipant[];
  village: number | WithId;
  crop: number | WithId;
  usage: number[] | WithId[];  // Changed to array for multiple selection
  sample_type: number | WithId;
  genetic_status: number | WithId | null;
  collection_source: number | WithId | null;
  material: number | WithId | null;
  topography: number | WithId | null;
  location_status: number | WithId | null;
  soil_texture: number | WithId | null;
  soil_color: number | WithId | null;
  hardness: number | WithId | null;
  drainage: number | WithId | null;
  soil_ph: number | WithId | null;
  water_ph: number | WithId | null;
  photos?: Array<{id: number; photo: string; description?: string; uploaded_at: string}>;  // Added photos field
  village_details?: {
    state?: number | WithId;
    state_details?: {
      governorate?: number | WithId;
    };
  };
  elevation?: number;
  location_description?: string;
  date_collected: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by?: number;
  diseases_pests: DiseasePest[];
  auto_latitude?: number;
  auto_longitude?: number;
  variety_number?: string;
  crop_age?: string;
  local_name?: string;
  is_local_seed: boolean;
  is_herbarium_sample: boolean;
  has_photos: boolean;
}

const SurveyForm: React.FC<{}> = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isEdit = Boolean(id);
  const surveyId = Number(id);
  const isRTL = i18n.language === 'ar';
  const currentLanguage = i18n.language;

  const steps = [
    { 
      title: t('basicInfoAndParticipants'), 
      icon: <InfoIcon />,
      description: t('surveyBasicInfoAndParticipants')
    },
    { 
      title: t('farmerAndLocation'), 
      icon: <LocationOnIcon />,
      description: t('farmerLocationInfo')
    },
    { 
      title: t('cropSampleAndSettings'), 
      icon: <AgricultureIcon />,
      description: t('cropSampleSettingsInfo')
    }
  ];

  // Form state
  const [formData, setFormData] = useState<SurveyFormData>({
    survey_number: '',
    date_collected: new Date().toISOString().split('T')[0],
    farmer: null,
    farmer_name: '',
    farmer_phone: '',
    participants: [],
    governorate: null,
    state: null,
    village: null,
    elevation: 0,
    auto_latitude: 0,
    auto_longitude: 0,
    location_description: '',
    crop: null,
    variety_number: '',
    local_name: '',
    crop_age: '',
    usage: [] as number[],  // Changed to empty array for multiple selection
    sample_type: null,
    genetic_status: null,
    collection_source: null,
    material: null,
    topography: null,
    location_status: null,
    soil_texture: null,
    soil_color: null,
    hardness: null,
    drainage: null,
    diseases_pests: [],
    soil_ph: null,
    water_ph: null,
    is_local_seed: false,
    is_herbarium_sample: false,
    has_photos: false,
    notes: ''
  });

  // Dropdown data
  const [governorates, setGovernorates] = useState<Governorate[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [villages, setVillages] = useState<Village[]>([]);
  const [crops, setCrops] = useState<Crop[]>([]);
  const [usages, setUsages] = useState<Usage[]>([]);
  const [sampleTypes, setSampleTypes] = useState<SampleType[]>([]);
  const [geneticStatuses, setGeneticStatuses] = useState<GeneticStatus[]>([]);
  const [collectionSources, setCollectionSources] = useState<CollectionSource[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [topographies, setTopographies] = useState<Topography[]>([]);
  const [locationStatuses, setLocationStatuses] = useState<LocationStatus[]>([]);
  const [soilTextures, setSoilTextures] = useState<SoilTexture[]>([]);
  const [soilColors, setSoilColors] = useState<SoilColor[]>([]);
  const [hardnesses, setHardnesses] = useState<Hardness[]>([]);
  const [drainages, setDrainages] = useState<Drainage[]>([]);
  const [diseasePests, setDiseasePests] = useState<DiseasePest[]>([]);
  const [soilPHs, setSoilPHs] = useState<SoilPH[]>([]);
  const [waterPHs, setWaterPHs] = useState<WaterPH[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [farmers, setFarmers] = useState<Farmer[]>([]);

  // UI state
  const [activeStep, setActiveStep] = useState(0);
  const [photos, setPhotos] = useState<File[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationInputMethod, setLocationInputMethod] = useState<'manual' | 'map'>('manual');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  // Location state
  const [selectedGovernorate, setSelectedGovernorate] = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<number | null>(null);

  // State for participant dialog
  const [participantDialog, setParticipantDialog] = useState<{
    open: boolean;
    editIndex: number;
    participant: {
      id: number;
      civilid: string;
      name: string;
      phone: string;
      department?: number; // قسم واحد رئيسي فقط
    };
  }>({
    open: false,
    editIndex: -1,
    participant: {
      id: 0,
      civilid: '',
      name: '',
      phone: '',
      department: undefined
    }
  });

  // State for existing participants
  const [existingParticipants, setExistingParticipants] = useState<Participant[]>([]);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    index: -1
  });

  // Farmer dialog state
  const [farmerDialog, setFarmerDialog] = useState<{
    open: boolean;
    farmer: {
      id?: number;
      civilid: string;
      name: string;
      phone: string;
      notes: string;
    };
  }>({
    open: false,
    farmer: {
      civilid: '',
      name: '',
      phone: '',
      notes: ''
    }
  });

  useEffect(() => {
    loadInitialData();
    if (isEdit && surveyId) {
      loadSurveyData(surveyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, surveyId]);

  useEffect(() => {
    if (selectedGovernorate !== null) {
      loadStates(selectedGovernorate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGovernorate]);

  useEffect(() => {
    if (selectedState !== null) {
      loadVillages(selectedState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState]);

  // تحديث حقول المزارع تلقائياً عند تحديد مزارع
  useEffect(() => {
    if (formData.farmer && farmers.length > 0) {
      const selectedFarmer = farmers.find(f => f.id === formData.farmer);
      if (selectedFarmer && (!formData.farmer_name || !formData.farmer_phone)) {
        console.log('Auto-updating farmer fields:', selectedFarmer);
        setFormData(prev => ({
          ...prev,
          farmer_name: selectedFarmer.name || prev.farmer_name,
          farmer_phone: selectedFarmer.phone || prev.farmer_phone
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.farmer, farmers]);

  const loadInitialData = async () => {
    try {
      console.log('Starting loadInitialData with single API call...');
      setDataLoading(true);
      
      // طلب واحد لجميع البيانات المطلوبة للنموذج
      const formData = await apiService.getSurveyFormData();
      console.log('All form data loaded in single request:', formData);
      
      // تعيين جميع البيانات
      setDepartments(formData.departments || []);
      setExistingParticipants(formData.participants || []);
      setFarmers(formData.farmers || []);
      setGovernorates(formData.governorates || []);
      setCrops(formData.crops || []);
      setUsages(formData.usages || []);
      setSampleTypes(formData.sample_types || []);
      setGeneticStatuses(formData.genetic_statuses || []);
      setCollectionSources(formData.collection_sources || []);
      setMaterials(formData.materials || []);
      setTopographies(formData.topographies || []);
      setLocationStatuses(formData.location_statuses || []);
      setSoilTextures(formData.soil_textures || []);
      setSoilColors(formData.soil_colors || []);
      setHardnesses(formData.hardnesses || []);
      setDrainages(formData.drainages || []);
      setDiseasePests(formData.disease_pests || []);
      setSoilPHs(formData.soil_phs || []);
      setWaterPHs(formData.water_phs || []);
      
      console.log('All data loaded successfully:', {
        departments: formData.departments?.length || 0,
        participants: formData.participants?.length || 0,
        farmers: formData.farmers?.length || 0,
        governorates: formData.governorates?.length || 0,
        crops: formData.crops?.length || 0,
        usages: formData.usages?.length || 0,
        sample_types: formData.sample_types?.length || 0,
        genetic_statuses: formData.genetic_statuses?.length || 0,
        collection_sources: formData.collection_sources?.length || 0,
        materials: formData.materials?.length || 0,
        topographies: formData.topographies?.length || 0,
        location_statuses: formData.location_statuses?.length || 0,
        soil_textures: formData.soil_textures?.length || 0,
        soil_colors: formData.soil_colors?.length || 0,
        hardnesses: formData.hardnesses?.length || 0,
        drainages: formData.drainages?.length || 0,
        disease_pests: formData.disease_pests?.length || 0,
        soil_phs: formData.soil_phs?.length || 0,
        water_phs: formData.water_phs?.length || 0,
      });
      
    } catch (error) {
      console.error('Error loading form data:', error);
      setError(t('errorLoadingData'));
      
      // في حالة فشل الطلب الموحد، نعود للطريقة القديمة كـ fallback
      console.log('Falling back to individual API calls...');
      try {
        const [
          departmentsData,
          participantsData,
          farmersData,
          governoratesData,
          cropsData,
          usagesData,
          sampleTypesData,
          geneticStatusesData,
          collectionSourcesData,
          materialsData,
          topographiesData,
          locationStatusesData,
          soilTexturesData,
          soilColorsData,
          hardnessesData,
          drainagesData,
          diseasePestsData,
          soilPHsData,
          waterPHsData
        ] = await Promise.all([
          apiService.getDepartments().catch(() => []),
          apiService.getParticipants().catch(() => []),
          apiService.getFarmers().catch(() => []),
          apiService.getGovernorates().catch(() => []),
          apiService.getCrops().catch(() => []),
          apiService.getUsages().catch(() => []),
          apiService.getSampleTypes().catch(() => []),
          apiService.getGeneticStatuses().catch(() => []),
          apiService.getCollectionSources().catch(() => []),
          apiService.getMaterials().catch(() => []),
          apiService.getTopographies().catch(() => []),
          apiService.getLocationStatuses().catch(() => []),
          apiService.getSoilTextures().catch(() => []),
          apiService.getSoilColors().catch(() => []),
          apiService.getHardnesses().catch(() => []),
          apiService.getDrainages().catch(() => []),
          apiService.getDiseasePests().catch(() => []),
          apiService.getSoilPHs().catch(() => []),
          apiService.getWaterPHs().catch(() => [])
        ]);
        
        setDepartments(departmentsData);
        setExistingParticipants(participantsData);
        setFarmers(farmersData);
        setGovernorates(governoratesData);
        setCrops(cropsData);
        setUsages(usagesData);
        setSampleTypes(sampleTypesData);
        setGeneticStatuses(geneticStatusesData);
        setCollectionSources(collectionSourcesData);
        setMaterials(materialsData);
        setTopographies(topographiesData);
        setLocationStatuses(locationStatusesData);
        setSoilTextures(soilTexturesData);
        setSoilColors(soilColorsData);
        setHardnesses(hardnessesData);
        setDrainages(drainagesData);
        setDiseasePests(diseasePestsData);
        setSoilPHs(soilPHsData);
        setWaterPHs(waterPHsData);
        
        console.log('Fallback loading completed');
      } catch (fallbackError) {
        console.error('Fallback loading also failed:', fallbackError);
      }
    } finally {
      setDataLoading(false);
    }
  };

  const loadGovernorates = async () => {
    try {
      console.log('Loading governorates...');
      const governoratesData = await apiService.getGovernorates();
      console.log('Governorates loaded:', governoratesData);
      setGovernorates(governoratesData);
      return governoratesData;
    } catch (error) {
      console.error('Error loading governorates:', error);
      return [];
    }
  };

  const loadStates = async (governorateId: number) => {
    try {
      console.log(`Loading states for governorate ID: ${governorateId}`);
      const statesData = await apiService.getStates(governorateId);
      console.log('States loaded:', statesData);
      setStates(statesData);
      
      // لا نريد مسح القرية إذا كنا نقوم بتحميل الاستبيان
      if (!isEdit) {
        setVillages([]); // Clear villages when governorate changes
        setSelectedState(null);
        setFormData(prev => ({ ...prev, state: null, village: null }));
      }
      return statesData;
    } catch (error) {
      console.error('Error loading states:', error);
      return [];
    }
  };

  const loadVillages = async (stateId: number) => {
    try {
      console.log(`Loading villages for state ID: ${stateId}`);
      const villagesData = await apiService.getVillages(stateId);
      console.log('Villages loaded:', villagesData);
      setVillages(villagesData);
    } catch (error) {
      console.error('Error loading villages:', error);
      setSnackbar({ open: true, message: t('errorLoadingVillages'), severity: 'error' });
    }
  };

  const loadSurveyData = async (id: number) => {
    try {
      setLoading(true);
      console.log(`Loading survey data for ID: ${id}`);
      const survey = await apiService.getSurvey(id) as unknown as ApiSurveyResponse;
      console.log('Survey data loaded successfully:', survey);
      console.log('========== DETAILED SURVEY DATA ==========');
      console.log('- Village:', JSON.stringify(survey.village));
      console.log('- Village Details:', JSON.stringify(survey.village_details));
      if (survey.village_details) {
        console.log('  - Village State:', JSON.stringify(survey.village_details.state));
        if (survey.village_details.state_details) {
          console.log('  - State Governorate:', JSON.stringify(survey.village_details.state_details.governorate));
        }
      }
      console.log('- Crop:', JSON.stringify(survey.crop));
      console.log('- Usage:', JSON.stringify(survey.usage));
      console.log('- Sample Type:', JSON.stringify(survey.sample_type));
      console.log('- Genetic Status:', JSON.stringify(survey.genetic_status));
      console.log('- Collection Source:', JSON.stringify(survey.collection_source));
      console.log('- Material:', JSON.stringify(survey.material));
      console.log('- Topography:', JSON.stringify(survey.topography));
      console.log('- Location Status:', JSON.stringify(survey.location_status));
      console.log('- Soil Texture:', JSON.stringify(survey.soil_texture));
      console.log('- Soil Color:', JSON.stringify(survey.soil_color));
      console.log('- Hardness:', JSON.stringify(survey.hardness));
      console.log('- Drainage:', JSON.stringify(survey.drainage));
      console.log('- Diseases/Pests:', JSON.stringify(survey.diseases_pests));
      console.log('- Soil pH:', JSON.stringify(survey.soil_ph));
      console.log('- Water pH:', JSON.stringify(survey.water_ph));
      console.log('Loading survey data:', survey);
      console.log('Participants:', survey.participants);
      console.log('Farmer data:', survey.farmer);
      
      // Convert SeedSurvey to SurveyFormData
      // دالة مساعدة لاستخراج المعرف من كائن أو استخدام القيمة مباشرة إذا كانت رقمًا
      const getIdFromObject = (obj: any): number | null => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj === 'number') return obj;
        if (typeof obj === 'object' && 'id' in obj) return obj.id;
        return null;
      };
      
      // إعداد بيانات النموذج
      const formDataToSet: SurveyFormData = {
        survey_number: survey.survey_number,
        date_collected: survey.date_collected,
        farmer: getIdFromObject(survey.farmer), // استخراج ID المزارع
        farmer_name: survey.farmer_name,
        farmer_phone: survey.farmer_phone,
        participants: survey.participants ? 
          survey.participants.map((sp: any) => {
            console.log('Processing survey participant:', sp);
            // تحويل بيانات المشارك إلى الصيغة المطلوبة للنموذج
            return {
              id: sp.id,
              civilid: sp.civilid || sp.participant?.civilid || '',
              name: sp.name || sp.participant?.name || '',
              phone: sp.phone || sp.participant?.phone || '',
              department: sp.department || sp.participant?.department || null,
              department_name_ar: sp.department_name_ar || sp.participant?.department_name_ar || '',
              department_name_en: sp.department_name_en || sp.participant?.department_name_en || ''
            };
          }) : [],
        governorate: null, // سيتم تحديثها لاحقًا
        state: null, // سيتم تحديثها لاحقًا
        village: getIdFromObject(survey.village),
        elevation: survey.elevation || 0,
        auto_latitude: survey.auto_latitude ? Number(survey.auto_latitude) : 0,
        auto_longitude: survey.auto_longitude ? Number(survey.auto_longitude) : 0,
        location_description: survey.location_description || '',
        crop: getIdFromObject(survey.crop),
        variety_number: survey.variety_number?.toString() || '',
        local_name: survey.local_name || '',
        crop_age: survey.crop_age || '',
        usage: Array.isArray(survey.usage) 
          ? survey.usage.map(u => getIdFromObject(u)).filter(id => id !== null) as number[] 
          : [getIdFromObject(survey.usage)].filter(id => id !== null) as number[],  // Handle both array and single object
        sample_type: getIdFromObject(survey.sample_type),
        genetic_status: getIdFromObject(survey.genetic_status),
        collection_source: getIdFromObject(survey.collection_source),
        material: getIdFromObject(survey.material),
        topography: getIdFromObject(survey.topography),
        location_status: getIdFromObject(survey.location_status),
        soil_texture: getIdFromObject(survey.soil_texture),
        soil_color: getIdFromObject(survey.soil_color),
        hardness: getIdFromObject(survey.hardness),
        drainage: getIdFromObject(survey.drainage),
        diseases_pests: survey.diseases_pests?.map(dp => typeof dp === 'object' && 'id' in dp ? dp.id : dp as number) || [],
        soil_ph: getIdFromObject(survey.soil_ph),
        water_ph: getIdFromObject(survey.water_ph),
        is_local_seed: survey.is_local_seed || false,
        is_herbarium_sample: survey.is_herbarium_sample || false,
        has_photos: survey.has_photos || false,
        notes: survey.notes || ''
      };
      
      console.log('Setting form data:', formDataToSet);
      
      console.log('Farmer data loaded:', {
        farmerId: getIdFromObject(survey.farmer),
        farmerName: survey.farmer_name,
        farmerPhone: survey.farmer_phone,
        farmerObject: survey.farmer
      });
      
      // إذا لم تكن البيانات موجودة في الحقول المباشرة، حاول استخراجها من كائن المزارع
      let farmerName = survey.farmer_name;
      let farmerPhone = survey.farmer_phone;
      
      if (survey.farmer && typeof survey.farmer === 'object' && 'name' in survey.farmer) {
        farmerName = farmerName || survey.farmer.name;
        farmerPhone = farmerPhone || survey.farmer.phone;
        console.log('Extracted from farmer object:', { farmerName, farmerPhone });
      }
      
      // تحديث البيانات مع القيم المستخرجة
      setFormData(prev => ({
        ...formDataToSet,
        farmer_name: farmerName || '',
        farmer_phone: farmerPhone || ''
      }));
      
      // معالجة بيانات الموقع للقوائم المنسدلة
      console.log('Processing location data for dropdowns');
      
      // استخراج معرفات الموقع بشكل مباشر من البيانات
      let governorateId: number | null = null;
      let stateId: number | null = null;
      let villageId: number | null = null;
      
      // استخراج معرف القرية
      if (survey.village) {
        if (typeof survey.village === 'object' && 'id' in survey.village) {
          villageId = survey.village.id;
          console.log('Village ID extracted from object:', villageId);
          
          // استخراج معرف الولاية من كائن القرية
          if ('state' in survey.village) {
            stateId = survey.village.state;
            console.log('State ID extracted from village object:', stateId);
          }
        } else if (typeof survey.village === 'number') {
          villageId = survey.village;
          console.log('Village ID extracted as number:', villageId);
        }
      }
      
      // استخراج معرف المحافظة من بيانات الاستجابة
      if (survey.village && typeof survey.village === 'object') {
        // استخراج معرف المحافظة من بيانات القرية
        if ('governorate_name_ar' in survey.village) {
          // البحث عن المحافظة بالاسم
          const govName = survey.village.governorate_name_ar as string;
          const foundGov = governorates.find(g => g.name_ar === govName);
          if (foundGov) {
            governorateId = foundGov.id;
            console.log('Governorate ID found by name:', governorateId);
          }
        }
      }
      
      console.log('Extracted governorate ID:', governorateId);
      console.log('Extracted state ID:', stateId);
      console.log('Extracted village ID:', villageId);
      
      // تحديث بيانات النموذج بالمعرفات المستخرجة
      formDataToSet.governorate = governorateId;
      formDataToSet.state = stateId;
      formDataToSet.village = villageId;
      
      console.log('Setting form data with location details:', formDataToSet);
      setFormData(formDataToSet);
      
      // تحميل البيانات المتسلسلة وتحديث القوائم المنسدلة
      try {
        console.log('Starting to load location data sequence...');
        
        // تحميل المحافظات أولاً إذا لم تكن محملة بالفعل
        if (governorates.length === 0) {
          console.log('Loading governorates first...');
          const govs = await loadGovernorates();
          
          // إذا لم يتم العثور على معرف المحافظة بالاسم، حاول مرة أخرى
          if (governorateId === null && survey.village && typeof survey.village === 'object' && 'governorate_name_ar' in survey.village) {
            const govName = survey.village.governorate_name_ar as string;
            const foundGov = govs.find(g => g.name_ar === govName);
            if (foundGov) {
              governorateId = foundGov.id;
              console.log('Governorate ID found by name after loading governorates:', governorateId);
            }
          }
        }
        
        // تحديث المحافظة المحددة
        if (governorateId !== null) {
          console.log('Setting selected governorate:', governorateId);
          setSelectedGovernorate(governorateId);
          
          // تحميل الولايات للمحافظة المحددة
          const statesData = await loadStates(governorateId);
          
          // إذا لم يتم العثور على معرف الولاية، حاول البحث عنها بالاسم
          if (stateId === null && survey.village && typeof survey.village === 'object' && 'state_name_ar' in survey.village) {
            const stateName = survey.village.state_name_ar as string;
            const foundState = statesData.find(s => s.name_ar === stateName);
            if (foundState) {
              stateId = foundState.id;
              console.log('State ID found by name after loading states:', stateId);
            }
          }
          
          // تحديث الولاية المحددة
          if (stateId !== null) {
            console.log('Setting selected state:', stateId);
            setSelectedState(stateId);
            
            // تحميل القرى للولاية المحددة
            await loadVillages(stateId);
          }
        }
        
        // تحديث بيانات النموذج مرة أخرى بعد تحميل البيانات المتسلسلة
        console.log('Updating form data with final location IDs:', { governorateId, stateId, villageId });
        setFormData(prev => ({
          ...prev,
          governorate: governorateId,
          state: stateId,
          village: villageId
        }));
      } catch (error) {
        console.error('Error loading location data:', error);
      }
      
      // تأكد من تحميل البيانات الأولية أولاً
      if (existingParticipants.length === 0 || crops.length === 0 || usages.length === 0) {
        console.log('Loading initial data first...');
        await loadInitialData();
      }

      console.log('Available participants after loading:', existingParticipants.length);
      console.log('Survey participants data:', survey.participants);

      // معالجة الصور الموجودة
      if (survey.photos && Array.isArray(survey.photos) && survey.photos.length > 0) {
        console.log('Found existing photos:', survey.photos.length);
        setExistingPhotos(survey.photos);
        setFormData(prev => ({ ...prev, has_photos: true }));
      } else {
        setExistingPhotos([]);
      }
    } catch (err) {
      setError(t('loadError'));
      console.error('Failed to load survey:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof SurveyFormData) => 
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown | number[]; checked?: boolean } }) => {
      const value = event.target.value;
      const checked = 'checked' in event.target ? event.target.checked : undefined;
      
      // Handle special cases for number fields
      if (field === 'auto_latitude' || field === 'auto_longitude' || field === 'elevation') {
        let numValue = Number(value) || 0;
        
        // تقييد الإحداثيات إلى 8 أرقام بعد الفاصلة
        if (field === 'auto_latitude' || field === 'auto_longitude') {
          numValue = parseFloat(numValue.toFixed(8));
        }
        
        setFormData(prev => ({
          ...prev,
          [field]: numValue
        }));
      } 
      // Special handling for usage field which supports multiple selection
      else if (field === 'usage') {
        setFormData(prev => {
          return {
            ...prev,
            usage: Array.isArray(value) ? value as number[] : []
          };
        });
      }
      // Handle dropdown fields that can be null
      else if (field === 'village' || field === 'crop' || field === 'sample_type' || field === 'genetic_status' || field === 'collection_source' || field === 'material' || field === 'topography' || field === 'location_status' || field === 'soil_texture' || field === 'soil_color' || field === 'hardness' || field === 'drainage' || field === 'soil_ph' || field === 'water_ph') {
        setFormData(prev => ({
          ...prev,
          [field]: value === "" ? null : Number(value)
        }));
      }
      // Handle boolean fields
      else if (field === 'is_local_seed' || field === 'is_herbarium_sample' || field === 'has_photos') {
        setFormData(prev => ({
          ...prev,
          [field]: checked !== undefined ? checked : Boolean(value)
        }));
      }
      // Handle string and other fields
      else {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      }
    };

  const handleLocationChange = async (type: 'governorate' | 'state', value: string | number) => {
    // تحويل القيمة إلى رقم أو null
    const numValue = value === "" ? null : Number(value);
    console.log(`Handling ${type} change to value:`, numValue);
    
    if (type === 'governorate') {
      setSelectedGovernorate(numValue);
      setSelectedState(null);
      setFormData(prev => ({ ...prev, governorate: numValue, state: null, village: null }));
      setStates([]);
      setVillages([]);
      
      if (numValue !== null) {
        try {
          console.log(`Loading states for governorate ID: ${numValue}`);
          const statesData = await apiService.getStates(numValue);
          console.log('States loaded:', statesData);
          setStates(statesData);
        } catch (err) {
          console.error('Failed to load states:', err);
        }
      }
    } else if (type === 'state') {
      setSelectedState(numValue);
      setFormData(prev => ({ ...prev, state: numValue, village: null }));
      setVillages([]);
      
      if (numValue !== null) {
        try {
          console.log(`Loading villages for state ID: ${numValue}`);
          const villagesData = await apiService.getVillages(numValue);
          console.log('Villages loaded:', villagesData);
          setVillages(villagesData);
        } catch (err) {
          console.error('Failed to load villages:', err);
        }
      }
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError(t('geolocationNotSupported'));
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // تقييد الإحداثيات إلى 8 أرقام بعد الفاصلة لتوافق قاعدة البيانات
        const latitude = parseFloat(position.coords.latitude.toFixed(8));
        const longitude = parseFloat(position.coords.longitude.toFixed(8));
        
        setFormData(prev => ({
          ...prev,
          auto_latitude: latitude,
          auto_longitude: longitude
        }));
        setGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setError(t('errorGettingLocation'));
        setGettingLocation(false);
      }
    );
  };

  // Participant management functions
  const openAddParticipantDialog = () => {
    setParticipantDialog({
      open: true,
      editIndex: -1,
      participant: {
        id: 0,
        civilid: '',
        name: '',
        phone: '',
        department: undefined
      }
    });
  };

  const openEditParticipantDialog = (index: number) => {
    const participant = formData.participants[index];
    
    setParticipantDialog({
      open: true,
      editIndex: index,
      participant: {
        id: participant.id || 0,
        civilid: participant.civilid || '',
        name: participant.name || '',
        phone: participant.phone || '',
        department: typeof participant.department === 'object' ? (participant.department as any)?.id : participant.department
      }
    });
  };

  const closeParticipantDialog = () => {
    setParticipantDialog({
      open: false,
      editIndex: -1,
      participant: {
        id: 0,
        civilid: '',
        name: '',
        phone: '',
        department: undefined
      }
    });
  };

  const handleParticipantInputChange = (field: keyof typeof participantDialog.participant) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    const value = event.target.value;
    setParticipantDialog(prev => ({
      ...prev,
      participant: {
        ...prev.participant,
        [field]: value
      }
    }));
  };

  const saveParticipant = async () => {
    const { participant, editIndex } = participantDialog;
    
    // Validation
    if (!participant.name.trim()) {
      setSnackbar({
        open: true,
        message: t('participantNameRequired'),
        severity: 'error'
      });
      return;
    }
    
    if (!participant.civilid.trim()) {
      setSnackbar({
        open: true,
        message: t('civilIdRequired'),
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);

      // Check if civil ID already exists in participants table
      const existingParticipant = await apiService.getParticipantByCivilId(participant.civilid.trim());
      
      if (existingParticipant && editIndex < 0) {
        // Civil ID exists and we're adding new participant
        setSnackbar({
          open: true,
          message: t('civilIdAlreadyExists'),
          severity: 'error'
        });
        return;
      }

      let savedParticipant: Participant;

      if (editIndex >= 0) {
        // Editing existing participant - just update local state
        const selectedDepartment = participant.department ? departments.find(d => d.id === participant.department) : null;
        
        const updatedParticipant: SurveyParticipant = {
          id: participant.id,
          civilid: participant.civilid.trim(),
          name: participant.name.trim(),
          phone: participant.phone.trim(),
          department: participant.department,
          department_name_ar: selectedDepartment?.name_ar,
          department_name_en: selectedDepartment?.name_en
        };

        setFormData(prev => {
          const newParticipants = [...prev.participants];
          newParticipants[editIndex] = updatedParticipant;
          return {
            ...prev,
            participants: newParticipants
          };
        });
      } else {
        // Adding new participant - save to participants table first
        const participantData = {
          civilid: participant.civilid.trim(),
          name: participant.name.trim(),
          phone: participant.phone.trim(),
          department: participant.department
        };

        savedParticipant = await apiService.createParticipant(participantData);

        // Add to form participants
        const selectedDepartment = participant.department ? departments.find(d => d.id === participant.department) : null;
        
        const newParticipant: SurveyParticipant = {
          id: savedParticipant.id,
          civilid: savedParticipant.civilid,
          name: savedParticipant.name,
          phone: savedParticipant.phone || '',
          department: savedParticipant.department,
          department_name_ar: selectedDepartment?.name_ar,
          department_name_en: selectedDepartment?.name_en
        };

        setFormData(prev => ({
          ...prev,
          participants: [...prev.participants, newParticipant]
        }));

        // Refresh existing participants list
        const updatedParticipants = await apiService.getParticipants();
        setExistingParticipants(updatedParticipants);
      }

      closeParticipantDialog();
      setSnackbar({
        open: true,
        message: editIndex >= 0 ? t('participantUpdated') : t('participantAddedAndSaved'),
        severity: 'success'
      });

    } catch (error: any) {
      console.error('Error saving participant:', error);
      let errorMessage = t('errorSavingParticipant');
      
      if (error.response?.data?.civilid) {
        errorMessage = t('civilIdAlreadyExists');
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const openDeleteConfirmDialog = (index: number) => {
    setDeleteConfirmDialog({
      open: true,
      index
    });
  };

  const closeDeleteConfirmDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      index: -1
    });
  };


  const addExistingParticipant = (participant: Participant) => {
    console.log('Adding existing participant:', participant);
    
    // Check if already added
    if (formData.participants.some(p => p.civilid === participant.civilid)) {
      setSnackbar({
        open: true,
        message: t('participantAlreadyAdded'),
        severity: 'error'
      });
      return;
    }

    const newParticipant: SurveyParticipant = {
      id: Date.now(),
      civilid: participant.civilid,
      name: participant.name,
      phone: participant.phone,
      department: participant.department,
      department_name_ar: participant.department_name_ar,
      department_name_en: participant.department_name_en
    };

    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, newParticipant]
    }));

    setSnackbar({
      open: true,
      message: t('participantAddedSuccessfully'),
      severity: 'success'
    });
  };

  const deleteParticipant = () => {
    const { index } = deleteConfirmDialog;
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter((_, i) => i !== index)
    }));
    closeDeleteConfirmDialog();
    setSnackbar({
      open: true,
      message: t('participantDeleted'),
      severity: 'success'
    });
  };

  // Farmer management functions
  const openFarmerDialog = () => {
    setFarmerDialog({
      open: true,
      farmer: {
        civilid: '',
        name: '',
        phone: '',
        notes: ''
      }
    });
  };

  const closeFarmerDialog = () => {
    setFarmerDialog({
      open: false,
      farmer: {
        civilid: '',
        name: '',
        phone: '',
        notes: ''
      }
    });
  };

  const handleFarmerInputChange = (field: keyof typeof farmerDialog.farmer) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    const value = event.target.value;
    setFarmerDialog(prev => ({
      ...prev,
      farmer: {
        ...prev.farmer,
        [field]: value
      }
    }));
  };

  const saveFarmer = async () => {
    const { farmer } = farmerDialog;
    
    // Validation
    if (!farmer.name.trim()) {
      setSnackbar({
        open: true,
        message: t('farmerNameRequired'),
        severity: 'error'
      });
      return;
    }

    if (!farmer.civilid.trim()) {
      setSnackbar({
        open: true,
        message: t('civilIdRequired'),
        severity: 'error'
      });
      return;
    }

    try {
      const farmerData = {
        civilid: farmer.civilid || `TEMP_${Date.now()}`,
        name: farmer.name,
        phone: farmer.phone || '',
        notes: farmer.notes || ''
      };
      const savedFarmer = await apiService.createFarmer(farmerData);
      setFarmers(prev => [...prev, savedFarmer]);
      setFormData(prev => ({
        ...prev,
        farmer: savedFarmer.id,
        farmer_name: savedFarmer.name,
        farmer_phone: savedFarmer.phone || ''
      }));
      closeFarmerDialog();
      setSnackbar({
        open: true,
        message: t('farmerCreated'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating farmer:', error);
      setSnackbar({
        open: true,
        message: t('errorCreatingFarmer'),
        severity: 'error'
      });
    }
  };

  // Validation functions for each step
  const validateStep0 = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Basic info validation
    if (!formData.date_collected) {
      errors.push(t('dateCollectedRequired'));
    }
    
    // Participants validation - at least one participant required
    if (formData.participants.length === 0) {
      errors.push(t('atLeastOneParticipantRequired'));
    }
    
    return { isValid: errors.length === 0, errors };
  };

  const validateStep1 = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Farmer validation
    if (!formData.farmer_name?.trim()) {
      errors.push(t('farmerNameRequired'));
    }
    if (!formData.farmer_phone?.trim()) {
      errors.push(t('farmerPhoneRequired'));
    }
    
    // Location validation
    if (!formData.governorate) {
      errors.push(t('governorateRequired'));
    }
    if (!formData.state) {
      errors.push(t('stateRequired'));
    }
    if (!formData.village) {
      errors.push(t('villageRequired'));
    }
    
    // Coordinates validation
    if (!formData.auto_latitude || formData.auto_latitude === 0) {
      errors.push(t('locationRequired'));
    }
    if (!formData.auto_longitude || formData.auto_longitude === 0) {
      errors.push(t('locationRequired'));
    }
    
    return { isValid: errors.length === 0, errors };
  };

  const validateStep2 = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Crop validation
    if (!formData.crop) {
      errors.push(t('cropRequired'));
    }
    if (!formData.variety_number?.trim()) {
      errors.push(t('varietyNumberRequired'));
    }
    if (!formData.crop_age?.trim()) {
      errors.push(t('cropAgeRequired'));
    }
    
    // Usage validation - at least one usage required
    if (!formData.usage || formData.usage.length === 0) {
      errors.push(t('usageRequired'));
    }
    
    // Sample type validation
    if (!formData.sample_type) {
      errors.push(t('sampleTypeRequired'));
    }
    
    // Required fields for soil and environment
    if (!formData.genetic_status) {
      errors.push(t('geneticStatusRequired'));
    }
    if (!formData.collection_source) {
      errors.push(t('collectionSourceRequired'));
    }
    if (!formData.material) {
      errors.push(t('materialRequired'));
    }
    if (!formData.topography) {
      errors.push(t('topographyRequired'));
    }
    if (!formData.location_status) {
      errors.push(t('locationStatusRequired'));
    }
    if (!formData.soil_texture) {
      errors.push(t('soilTextureRequired'));
    }
    if (!formData.soil_color) {
      errors.push(t('soilColorRequired'));
    }
    if (!formData.hardness) {
      errors.push(t('hardnessRequired'));
    }
    if (!formData.drainage) {
      errors.push(t('drainageRequired'));
    }
    
    return { isValid: errors.length === 0, errors };
  };

  const validateCurrentStep = (): { isValid: boolean; errors: string[] } => {
    switch (activeStep) {
      case 0:
        return validateStep0();
      case 1:
        return validateStep1();
      case 2:
        return validateStep2();
      default:
        return { isValid: true, errors: [] };
    }
  };

  const handleNext = () => {
    const validation = validateCurrentStep();
    
    if (!validation.isValid) {
      // Show validation errors
      setSnackbar({
        open: true,
        message: validation.errors.join('\n'),
        severity: 'error'
      });
      return;
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Check if current step is valid for button state
  const isCurrentStepValid = (): boolean => {
    const validation = validateCurrentStep();
    return validation.isValid;
  };

  // دالة لإنشاء أنماط الحقول مع دعم RTL وتكبير الخط
  const getFieldStyles = () => ({
    direction: isRTL ? 'rtl' : 'ltr',
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      fontSize: '18px', // تكبير الخط
      direction: isRTL ? 'rtl' : 'ltr',
      '& fieldset': {
        borderColor: '#d1d5db',
      },
      '&:hover fieldset': {
        borderColor: '#9ca3af',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
        borderWidth: 2,
      },
      '& input': {
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
        fontSize: '18px !important', // تكبير خط النص
        fontFamily: isRTL ? 'Tahoma, Arial, sans-serif' : 'inherit', // خط عربي أفضل
        unicodeBidi: 'plaintext', // دعم النصوص المختلطة
      },
      '& textarea': {
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
        fontSize: '18px !important', // تكبير خط النص
        fontFamily: isRTL ? 'Tahoma, Arial, sans-serif' : 'inherit', // خط عربي أفضل
        unicodeBidi: 'plaintext', // دعم النصوص المختلطة
      },
    },
    '& .MuiSelect-select': {
      direction: isRTL ? 'rtl' : 'ltr',
      textAlign: isRTL ? 'right' : 'left',
      fontSize: '18px !important', // تكبير خط النص
      fontFamily: isRTL ? 'Tahoma, Arial, sans-serif' : 'inherit', // خط عربي أفضل
      paddingRight: isRTL ? '14px' : '32px', // تعديل المسافة للأيقونة
      paddingLeft: isRTL ? '32px' : '14px', // تعديل المسافة للأيقونة
    },
    '& .MuiSelect-icon': {
      right: isRTL ? 'auto' : 7, // موضع الأيقونة في LTR
      left: isRTL ? 7 : 'auto', // موضع الأيقونة في RTL
    },
  });

  const handleSubmit = async () => {
    // التحقق من صحة جميع البيانات قبل الإرسال
    const step0Validation = validateStep0();
    const step1Validation = validateStep1();
    const step2Validation = validateStep2();
    
    const allErrors = [
      ...step0Validation.errors,
      ...step1Validation.errors,
      ...step2Validation.errors
    ];
    
    if (allErrors.length > 0) {
      setSnackbar({
        open: true,
        message: allErrors.join('\n'),
        severity: 'error'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const apiData: any = {
        date_collected: formData.date_collected,
        variety_number: formData.variety_number,
        crop_age: formData.crop_age,
        is_herbarium_sample: formData.is_herbarium_sample,
        is_local_seed: formData.is_local_seed,
        has_photos: formData.has_photos,
        auto_latitude: formData.auto_latitude ? parseFloat(Number(formData.auto_latitude).toFixed(8)) : formData.auto_latitude,
        auto_longitude: formData.auto_longitude ? parseFloat(Number(formData.auto_longitude).toFixed(8)) : formData.auto_longitude,
        elevation: formData.elevation,
        location_description: formData.location_description,
        diseases_pests: formData.diseases_pests || []
      };

      // إرسال المشاركين
      console.log('=== SENDING PARTICIPANTS ===');
      console.log('Current formData.participants:', formData.participants);
      
      const participantsForAPI = formData.participants
        .filter(participant => 
          participant.civilid && 
          participant.civilid.trim() !== '' && 
          participant.name && 
          participant.name.trim() !== '' && 
          participant.phone && 
          participant.phone.trim() !== ''
        )
        .map(participant => ({
          civilid: participant.civilid!.trim(),
          name: participant.name!.trim(),
          phone: participant.phone!.trim(),
          department: participant.department
        }));
      
      apiData.participants = participantsForAPI;
      
      // إرسال replace_all_participants فقط عند التحديث وليس الإنشاء
      if (isEdit) {
        apiData.replace_all_participants = true;
      }
      
      console.log('Participants being sent to API:', participantsForAPI);
      console.log('=== END PARTICIPANTS ===');

      // إضافة survey_number فقط إذا كان موجوداً (للتحديث)
      if (formData.survey_number && formData.survey_number.trim() !== '') {
        apiData.survey_number = formData.survey_number;
      }

      // إضافة جميع الحقول حتى لو كانت null (للحقول الاختيارية)
      apiData.farmer = formData.farmer;
      apiData.village = formData.village;
      apiData.crop = formData.crop;
      apiData.usage = formData.usage || [];
      apiData.sample_type = formData.sample_type;
      apiData.genetic_status = formData.genetic_status;
      apiData.collection_source = formData.collection_source;
      apiData.material = formData.material;
      apiData.topography = formData.topography;
      apiData.location_status = formData.location_status;
      apiData.soil_texture = formData.soil_texture;
      apiData.soil_color = formData.soil_color;
      apiData.hardness = formData.hardness;
      apiData.drainage = formData.drainage;
      apiData.soil_ph = formData.soil_ph;
      apiData.water_ph = formData.water_ph;
      
      // طباعة البيانات للتشخيص
      console.log('FormData:', formData);
      console.log('API Data being sent:', JSON.stringify(apiData, null, 2));
      
      let savedSurvey: SeedSurvey;

      if (isEdit && surveyId) {
        savedSurvey = await apiService.updateSurvey(surveyId, apiData);
        setSuccess(t('surveyUpdated'));
      } else {
        savedSurvey = await apiService.createSurvey(apiData);
        setSuccess(t('surveyCreated'));
      }

      if (savedSurvey && savedSurvey.id && formData.has_photos && photos.length > 0) {
        try {
          await apiService.uploadSurveyPhotos(savedSurvey.id, photos);
          setSnackbar({
            open: true,
            message: t('photosUploadedSuccessfully'),
            severity: 'success'
          });
        } catch (photoError) {
          console.error('Error uploading photos:', photoError);
          setError(t('errorUploadingPhotos'));
          setLoading(false);
          return;
        }
      }

      navigate('/surveys');
    } catch (error) {
      console.error('Error saving survey:', error);
      setError(t('errorSavingSurvey'));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    const fieldStyles = getFieldStyles();

    switch (activeStep) {
      case 0: // Basic Info + Participants
        return (
          <Box>
            {/* Basic Survey Information */}
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              📋 {t('basicInfo')}
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 2, sm: 2.5, md: 3 },
              mb: { xs: 3, sm: 4 }
            }}>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: { xs: '16px', sm: '17px', md: '18px' },
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('surveyNumber')}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.survey_number}
                  onChange={handleInputChange('survey_number')}
                  sx={fieldStyles}
                  disabled={true}
                  helperText={t('surveyNumberAutoGenerated')}
                  placeholder={t('surveyNumber')}
                  inputProps={{
                    dir: isRTL ? 'rtl' : 'ltr',
                    style: {
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }
                  }}
                />
              </Box>
                <Box>
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    fontSize: '18px',
                    color: '#374151',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('dateCollected')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    required
                    value={formData.date_collected}
                    onChange={handleInputChange('date_collected')}
                    sx={fieldStyles}
                    placeholder={t('dateCollected')}
                    error={!formData.date_collected}
                    helperText={!formData.date_collected ? t('dateCollectedRequired') : ''}
                    inputProps={{
                      dir: isRTL ? 'rtl' : 'ltr',
                      style: {
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }
                    }}
                  />
              </Box>
            </Box>

            {/* Participants Section */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              👥 {t('participants')}
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              {/* Existing Participants Dropdown */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ 
                  mb: 1, 
                  color: '#6b7280',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('selectExistingParticipant')}
                </Typography>
                {dataLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      {t('loading', 'Loading participants...')}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1, color: existingParticipants.length > 0 ? 'green' : 'red' }}>
                      {t('availableParticipants')}: {existingParticipants.length}
                    </Typography>
                    <Autocomplete
                    options={existingParticipants || []}
                    getOptionLabel={(option) => `${option?.name || ''} (${option?.civilid || ''})`}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter(option =>
                        option.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.civilid?.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.phone?.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.department_name_ar?.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.department_name_en?.toLowerCase().includes(inputValue.toLowerCase())
                      );
                    }}
                    onChange={(_, participant) => {
                      if (participant) {
                        addExistingParticipant(participant as Participant);
                      }
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {option.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          📱 {option.phone}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          🆔 {option.civilid}
                        </Typography>
                        {(option.department_name_ar || option.department_name_en) && (
                          <Typography variant="body2" color="primary.main">
                            🏢 {i18n.language === 'ar' ? option.department_name_ar : option.department_name_en}
                          </Typography>
                        )}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        placeholder={t('searchExistingParticipant')}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f8fafc'
                          }
                        }}
                      />
                    )}
                  />
                  </>
                )}
              </Box>
              
              {/* Add New Participant Button */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={() => openAddParticipantDialog()}
                  sx={{ borderRadius: 2, textTransform: 'none', py: 1, justifyContent: 'flex-start' }}
                >
                  {t('addParticipant')}
                </Button>
              </Box>
            </Box>

            {/* Participants Section - Redesigned */}
            <Box sx={{ 
              p: 3, 
              mb: 3,
              bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 3,
                p: 2,
                bgcolor: 'white',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: '50%', 
                    bgcolor: '#667eea',
                    color: 'white'
                  }}>
                    <PersonIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: '#1e293b', 
                      fontWeight: 700,
                      mb: 0.5
                    }}>
                      {t('participants')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('manageParticipants')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip 
                    label={`${formData.participants.length} ${t('participantSingle')}`}
                    size="medium" 
                    color={formData.participants.length > 0 ? "success" : "default"}
                    sx={{ 
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 2 }
                    }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAddParticipantDialog}
                    sx={{
                      bgcolor: '#10b981',
                      '&:hover': { bgcolor: '#059669' },
                      borderRadius: 2,
                      px: 3
                    }}
                  >
                    {t('addParticipant')}
                  </Button>
                </Box>
              </Box>

              {/* Participants Display */}
              {formData.participants.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center',
                  p: 6,
                  bgcolor: 'white',
                  borderRadius: 2,
                  border: '2px dashed #cbd5e1'
                }}>
                  <PersonIcon sx={{ 
                    fontSize: 64, 
                    color: '#94a3b8',
                    mb: 2
                  }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    {t('noParticipants')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('addFirstParticipant')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openAddParticipantDialog}
                    sx={{
                      borderColor: '#667eea',
                      color: '#667eea',
                      '&:hover': { 
                        bgcolor: '#667eea',
                        color: 'white'
                      }
                    }}
                  >
                    {t('addParticipant')}
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {formData.participants.map((participant, index) => {
                    const name = participant.name || '';
                    const phone = participant.phone || '';
                    const civilId = participant.civilid || '';
                    
                    const departmentNameAr = participant.department_name_ar;
                    const departmentNameEn = participant.department_name_en;
                    
                    let departmentDisplay = '';
                    if (departmentNameAr || departmentNameEn) {
                      departmentDisplay = i18n.language === 'ar' ? 
                        (departmentNameAr || departmentNameEn || '') : 
                        (departmentNameEn || departmentNameAr || '');
                    }
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={participant.id || civilId || index}>
                        <Card sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            borderColor: '#667eea'
                          }
                        }}>
                          <Box sx={{ p: 2.5, flexGrow: 1 }}>
                            {/* Participant Header */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 2,
                              pb: 1.5,
                              borderBottom: '1px solid #f1f5f9'
                            }}>
                              <Box sx={{ 
                                p: 1, 
                                borderRadius: '50%', 
                                bgcolor: '#f0f9ff',
                                color: '#0369a1',
                                mr: 1.5
                              }}>
                                <PersonIcon fontSize="small" />
                              </Box>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" sx={{ 
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  mb: 0.5
                                }}>
                                  {name}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  color: '#64748b',
                                  bgcolor: '#f8fafc',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontWeight: 500
                                }}>
                                  {t('civilId')}: {civilId}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Contact Info */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                                color: '#475569'
                              }}>
                                📞 {phone || t('noPhone')}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: departmentDisplay ? '#475569' : '#94a3b8',
                                fontStyle: departmentDisplay ? 'normal' : 'italic'
                              }}>
                                🏢 {departmentDisplay || t('noDepartment')}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Action Buttons */}
                          <Box sx={{ 
                            p: 2,
                            pt: 0,
                            display: 'flex',
                            gap: 1
                          }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => openEditParticipantDialog(index)}
                              sx={{
                                flex: 1,
                                borderColor: '#f59e0b',
                                color: '#f59e0b',
                                '&:hover': { 
                                  bgcolor: '#fef3c7',
                                  borderColor: '#f59e0b'
                                }
                              }}
                            >
                              {t('edit')}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => openDeleteConfirmDialog(index)}
                              sx={{
                                flex: 1,
                                borderColor: '#ef4444',
                                color: '#ef4444',
                                '&:hover': { 
                                  bgcolor: '#fee2e2',
                                  borderColor: '#ef4444'
                                }
                              }}
                            >
                              {t('delete')}
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Box>
        );

      case 1: // Farmer and Location
        return (
          <Box>
            {/* Farmer Information */}
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              👨‍🌾 {t('farmerInfo')}
            </Typography>
            
            {/* Enhanced Farmer Selection Card */}
            <Paper sx={{ 
              p: 3, 
              mb: 4,
              bgcolor: '#f8fafc', 
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              {/* Header with status and action */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600,
                    color: '#374151'
                  }}>
                    {t('selectFarmer')}
                  </Typography>
                  <Chip 
                    label={`${farmers.length} ${t('availableFarmers')}`}
                    size="small" 
                    color={farmers.length > 0 ? 'success' : 'default'}
                    icon={farmers.length > 0 ? <PersonIcon /> : undefined}
                  />
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openFarmerDialog}
                  startIcon={<AddIcon />}
                  sx={{
                    bgcolor: '#10b981',
                    '&:hover': { bgcolor: '#059669' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  {t('addNewFarmer')}
                </Button>
              </Box>

              {/* Farmer Selection Autocomplete */}
              <Autocomplete
                options={farmers}
                getOptionLabel={(farmer) => farmer.name}
                value={farmers.find(f => f.id === formData.farmer) || null}
                onChange={(event, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    farmer: newValue?.id || null,
                    farmer_name: newValue?.name || '',
                    farmer_phone: newValue?.phone || ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('searchOrSelectFarmer')}
                    placeholder={t('typeToSearchFarmer')}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#10b981'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#10b981'
                        }
                      }
                    }}
                    inputProps={{
                      ...params.inputProps,
                      dir: isRTL ? 'rtl' : 'ltr',
                      style: {
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }
                    }}
                  />
                )}
                renderOption={(props, farmer) => (
                  <Box component="li" {...props} sx={{ 
                    direction: isRTL ? 'rtl' : 'ltr',
                    p: 2,
                    borderBottom: '1px solid #f1f5f9',
                    '&:hover': {
                      bgcolor: '#f8fafc'
                    }
                  }}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 600,
                        color: '#1e293b',
                        textAlign: isRTL ? 'right' : 'left',
                        mb: 0.5
                      }}>
                        {farmer.name}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: isRTL ? 'flex-end' : 'flex-start',
                        gap: 3,
                        flexWrap: 'wrap'
                      }}>
                        {farmer.civilid && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ 
                              color: '#6b7280',
                              fontWeight: 500
                            }}>
                              {t('civilId')}:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: '#374151',
                              fontWeight: 500
                            }}>
                              {farmer.civilid}
                            </Typography>
                          </Box>
                        )}
                        {farmer.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ 
                              color: '#6b7280',
                              fontWeight: 500
                            }}>
                              {t('phone')}:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: '#374151',
                              fontWeight: 500
                            }}>
                              {farmer.phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(farmer => 
                    farmer.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (farmer.civilid && farmer.civilid.includes(inputValue)) ||
                    (farmer.phone && farmer.phone.includes(inputValue))
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={t('noFarmersFound')}
                sx={{
                  '& .MuiAutocomplete-listbox': {
                    direction: isRTL ? 'rtl' : 'ltr',
                    maxHeight: 300,
                    p: 0
                  },
                  '& .MuiAutocomplete-paper': {
                    borderRadius: 2,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }
                }}
              />

              {/* Selected Farmer Display */}
              {formData.farmer && (
                <Box sx={{ 
                  mt: 3,
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 2,
                  border: '1px solid #d1fae5'
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    color: '#059669',
                    fontWeight: 600,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    ✅ {t('selectedFarmer')}
                  </Typography>
                  <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 2
                  }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        {t('farmerName')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>
                        {formData.farmer_name}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        {t('civilId')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>
                        {farmers.find(f => f.id === formData.farmer)?.civilid || t('notProvided')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        {t('farmerPhone')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>
                        {formData.farmer_phone || t('notProvided')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Location Information */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              📍 {t('locationInfo')}
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
              mb: 4
            }}>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('governorate')}
                </Typography>
                <Autocomplete
                  fullWidth
                  options={governorates || []}
                  value={governorates?.find(gov => gov.id === formData.governorate) || null}
                  onChange={(_, selectedGovernorate) => {
                    const value = selectedGovernorate ? selectedGovernorate.id : '';
                    handleLocationChange('governorate', value);
                  }}
                  getOptionLabel={(option) => currentLanguage === 'ar' ? option.name_ar : option.name_en}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={!formData.governorate}
                      placeholder={t('searchOrSelectGovernorate')}
                      sx={getFieldStyles()}
                      inputProps={{
                        ...params.inputProps,
                        dir: isRTL ? 'rtl' : 'ltr',
                        style: {
                          textAlign: isRTL ? 'right' : 'left',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ 
                      display: 'flex', 
                      justifyContent: isRTL ? 'flex-end' : 'flex-start',
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}>
                      <Typography variant="body2">
                        {currentLanguage === 'ar' ? option.name_ar : option.name_en}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText={t('noGovernoratesFound')}
                  sx={{
                    '& .MuiAutocomplete-listbox': {
                      direction: isRTL ? 'rtl' : 'ltr'
                    },
                    '& .MuiAutocomplete-paper': {
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                />
                {!formData.governorate && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                    {t('governorateRequired')}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('state')}
                </Typography>
                <Autocomplete
                  fullWidth
                  options={states || []}
                  value={states?.find(state => state.id === formData.state) || null}
                  onChange={(_, selectedState) => {
                    const value = selectedState ? selectedState.id : '';
                    handleLocationChange('state', value);
                  }}
                  getOptionLabel={(option) => currentLanguage === 'ar' ? option.name_ar : option.name_en}
                  disabled={formData.governorate === null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={!formData.state && formData.governorate !== null}
                      placeholder={formData.governorate === null ? t('selectGovernorateFirst') : t('searchOrSelectState')}
                      sx={getFieldStyles()}
                      inputProps={{
                        ...params.inputProps,
                        dir: isRTL ? 'rtl' : 'ltr',
                        style: {
                          textAlign: isRTL ? 'right' : 'left',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ 
                      display: 'flex', 
                      justifyContent: isRTL ? 'flex-end' : 'flex-start',
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}>
                      <Typography variant="body2">
                        {currentLanguage === 'ar' ? option.name_ar : option.name_en}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText={formData.governorate === null ? t('selectGovernorateFirst') : t('noStatesFound')}
                  sx={{
                    '& .MuiAutocomplete-listbox': {
                      direction: isRTL ? 'rtl' : 'ltr'
                    },
                    '& .MuiAutocomplete-paper': {
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                />
                {!formData.state && formData.governorate && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                    {t('stateRequired')}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('village')}
                </Typography>
                <Autocomplete
                  fullWidth
                  options={villages || []}
                  value={villages?.find(village => village.id === formData.village) || null}
                  onChange={(_, selectedVillage) => {
                    const value = selectedVillage ? selectedVillage.id : null;
                    setFormData(prev => ({ ...prev, village: value }));
                  }}
                  getOptionLabel={(option) => currentLanguage === 'ar' ? option.name_ar : option.name_en}
                  disabled={formData.state === null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={!formData.village && formData.state !== null}
                      placeholder={formData.state === null ? t('selectStateFirst') : t('searchOrSelectVillage')}
                      sx={getFieldStyles()}
                      inputProps={{
                        ...params.inputProps,
                        dir: isRTL ? 'rtl' : 'ltr',
                        style: {
                          textAlign: isRTL ? 'right' : 'left',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ 
                      display: 'flex', 
                      justifyContent: isRTL ? 'flex-end' : 'flex-start',
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}>
                      <Typography variant="body2">
                        {currentLanguage === 'ar' ? option.name_ar : option.name_en}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText={formData.state === null ? t('selectStateFirst') : t('noVillagesFound')}
                  sx={{
                    '& .MuiAutocomplete-listbox': {
                      direction: isRTL ? 'rtl' : 'ltr'
                    },
                    '& .MuiAutocomplete-paper': {
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                />
                {!formData.village && formData.state && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                    {t('villageRequired')}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Coordinates and Additional Location Info */}
            <Paper sx={{ 
              p: 3, 
              bgcolor: '#f8fafc', 
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Typography variant="subtitle1" sx={{ 
                mb: 3, 
                color: '#1e293b', 
                fontWeight: 600
              }}>
                🗺️ {t('coordinatesAndDetails')}
              </Typography>

              {/* Location Input Method Toggle */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant={locationInputMethod === 'manual' ? 'contained' : 'outlined'}
                  onClick={() => setLocationInputMethod('manual')}
                  sx={{
                    bgcolor: locationInputMethod === 'manual' ? '#3b82f6' : 'transparent',
                    color: locationInputMethod === 'manual' ? '#fff' : '#3b82f6',
                    '&:hover': {
                      bgcolor: locationInputMethod === 'manual' ? '#2563eb' : 'rgba(59, 130, 246, 0.08)'
                    },
                    borderColor: '#3b82f6',
                    borderRadius: 2,
                    px: 3,
                    py: 1
                  }}
                >
                  {t('manualEntry')}
                </Button>
                <Button
                  variant={locationInputMethod === 'map' ? 'contained' : 'outlined'}
                  onClick={() => setLocationInputMethod('map')}
                  sx={{
                    bgcolor: locationInputMethod === 'map' ? '#10b981' : 'transparent',
                    color: locationInputMethod === 'map' ? '#fff' : '#10b981',
                    '&:hover': {
                      bgcolor: locationInputMethod === 'map' ? '#059669' : 'rgba(16, 185, 129, 0.08)'
                    },
                    borderColor: '#10b981',
                    borderRadius: 2,
                    px: 3,
                    py: 1
                  }}
                >
                  {t('mapSelection')}
                </Button>
              </Box>

              {locationInputMethod === 'manual' ? (
                // Manual input mode
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 3
              }}>
                <Box>
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    fontSize: '18px',
                    color: '#374151',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('autoLatitude')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    required
                    value={formData.auto_latitude || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // تقييد إلى 8 منازل عشرية كحد أقصى مع دعم الأرقام السالبة
                      if (value === '' || /^-?\d*\.?\d{0,8}$/.test(value)) {
                        handleInputChange('auto_latitude')(e);
                      }
                    }}
                    sx={getFieldStyles()}
                    error={!formData.auto_latitude || formData.auto_latitude === 0}
                    inputProps={{ 
                      step: 0.00000001,
                      min: -90,
                      max: 90,
                      dir: isRTL ? 'rtl' : 'ltr',
                      style: {
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }
                    }}
                    placeholder="xx.xxxxxxxx"
                    helperText={(!formData.auto_latitude || formData.auto_latitude === 0) ? t('locationRequired') : t('latitudeRange')}
                  />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    fontSize: '18px',
                    color: '#374151',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('autoLongitude')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    required
                    value={formData.auto_longitude || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // تقييد إلى 8 منازل عشرية كحد أقصى مع دعم الأرقام السالبة
                      if (value === '' || /^-?\d*\.?\d{0,8}$/.test(value)) {
                        handleInputChange('auto_longitude')(e);
                      }
                    }}
                    sx={getFieldStyles()}
                    error={!formData.auto_longitude || formData.auto_longitude === 0}
                    inputProps={{ 
                      step: 0.00000001,
                      min: -180,
                      max: 180,
                      dir: isRTL ? 'rtl' : 'ltr',
                      style: {
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }
                    }}
                    placeholder="xxx.xxxxxxxx"
                    helperText={(!formData.auto_longitude || formData.auto_longitude === 0) ? t('locationRequired') : t('longitudeRange')}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={gettingLocation ? <CircularProgress size={20} color="inherit" /> : <MyLocationIcon />}
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  sx={{
                    bgcolor: '#10b981',
                    '&:hover': { bgcolor: '#059669' },
                    '&:disabled': { bgcolor: '#9ca3af' },
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    minWidth: 200
                  }}
                >
                  {gettingLocation ? t('gettingLocation') : t('getCurrentLocation')}
                </Button>
                <Box>
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    fontSize: '18px',
                    color: '#374151',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('elevation')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.elevation || ''}
                    onChange={handleInputChange('elevation')}
                    sx={getFieldStyles()}
                    placeholder={t('elevationPlaceholder')}
                    inputProps={{
                      dir: isRTL ? 'rtl' : 'ltr',
                      style: {
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    fontSize: '18px',
                    color: '#374151',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('locationDescription')}
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.location_description || ''}
                    onChange={handleInputChange('location_description')}
                    sx={getFieldStyles()}
                    multiline
                    rows={3}
                    inputProps={{
                      dir: isRTL ? 'rtl' : 'ltr',
                      style: {
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }
                    }}
                  />
                </Box>
              </Box>
              ) : (
                // Map selection mode
                <Box>
                  <MapPicker
                    latitude={formData.auto_latitude}
                    longitude={formData.auto_longitude}
                    onChange={(lat, lng) => {
                      setFormData({
                        ...formData,
                        auto_latitude: lat,
                        auto_longitude: lng
                      });
                    }}
                    height={400}
                  />
                </Box>
              )}
            </Paper>
          </Box>
        );

      case 2: // Crop, Sample & Settings
        return (
          <Box>
            {/* Crop Information */}
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              🌾 {t('cropInfo')}
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
              mb: 4
            }}>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('crop')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.crop}>
                  <Select
                    value={formData.crop || ""}
                    onChange={handleInputChange('crop')}
                    displayEmpty
                    error={!formData.crop}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {crops?.map((crop) => (
                      <MenuItem key={crop.id} value={crop.id}>
                        {currentLanguage === 'ar' ? crop.name_ar : crop.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.crop && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('cropRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('varietyNumber')}
                </Typography>
                <TextField
                  fullWidth
                  required
                  value={formData.variety_number || ''}
                  onChange={(e) => {
                    // Only allow numbers
                    if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
                      handleInputChange('variety_number')(e);
                    }
                  }}
                  placeholder={t('varietyNumber')}
                  error={!formData.variety_number?.trim()}
                  helperText={!formData.variety_number?.trim() ? t('varietyNumberRequired') : ''}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                  sx={getFieldStyles()}
                />
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('cropAge')}
                </Typography>
                <TextField
                  fullWidth
                  required
                  value={formData.crop_age || ''}
                  onChange={(e) => {
                    // Only allow numbers
                    if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
                      handleInputChange('crop_age')(e);
                    }
                  }}
                  placeholder={t('cropAge')}
                  error={!formData.crop_age?.trim()}
                  helperText={!formData.crop_age?.trim() ? t('cropAgeRequired') : ''}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                  sx={getFieldStyles()}
                />
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('usage')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.usage || formData.usage.length === 0}>
                  <Select
                    multiple
                    value={Array.isArray(formData.usage) ? formData.usage : []}
                    onChange={handleInputChange('usage')}
                    displayEmpty
                    error={!formData.usage || formData.usage.length === 0}
                    renderValue={(selected) => {
                      if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                        return <em>{t('pleaseSelect')}</em>;
                      }
                      
                      return Array.isArray(selected) 
                        ? selected.map(id => {
                            const selectedUsage = usages.find(u => u.id === id);
                            return currentLanguage === 'ar' 
                              ? selectedUsage?.name_ar 
                              : selectedUsage?.name_en;
                          }).join(', ')
                        : '';
                    }}
                  >
                    {usages?.map((usage) => (
                      <MenuItem key={usage.id} value={usage.id}>
                        <Checkbox checked={Array.isArray(formData.usage) && formData.usage.indexOf(usage.id) > -1} />
                        {currentLanguage === 'ar' ? usage.name_ar : usage.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {(!formData.usage || formData.usage.length === 0) && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('usageRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Sample Information */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              📦 {t('sampleInfo')}
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3,
              mb: 4
            }}>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('sampleType')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.sample_type}>
                  <Select
                    value={formData.sample_type || ""}
                    onChange={handleInputChange('sample_type')}
                    displayEmpty
                    error={!formData.sample_type}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {sampleTypes?.map((sampleType) => (
                      <MenuItem key={sampleType.id} value={sampleType.id}>
                        {currentLanguage === 'ar' ? sampleType.name_ar : sampleType.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.sample_type && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('sampleTypeRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('geneticStatus')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.genetic_status}>
                  <Select
                    value={formData.genetic_status || ""}
                    onChange={handleInputChange('genetic_status')}
                    displayEmpty
                    error={!formData.genetic_status}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {geneticStatuses?.map((geneticStatus) => (
                      <MenuItem key={geneticStatus.id} value={geneticStatus.id}>
                        {currentLanguage === 'ar' ? geneticStatus.name_ar : geneticStatus.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.genetic_status && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('geneticStatusRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('collectionSource')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.collection_source}>
                  <Select
                    value={formData.collection_source || ""}
                    onChange={handleInputChange('collection_source')}
                    displayEmpty
                    error={!formData.collection_source}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {collectionSources?.map((collectionSource) => (
                      <MenuItem key={collectionSource.id} value={collectionSource.id}>
                        {currentLanguage === 'ar' ? collectionSource.name_ar : collectionSource.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.collection_source && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('collectionSourceRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('material')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.material}>
                  <Select
                    value={formData.material || ""}
                    onChange={handleInputChange('material')}
                    displayEmpty
                    error={!formData.material}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {materials?.map((material) => (
                      <MenuItem key={material.id} value={material.id}>
                        {currentLanguage === 'ar' ? material.name_ar : material.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.material && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('materialRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Soil and Environmental Information */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              🌱 {t('soilAndEnvironment')}
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3,
              mb: 4
            }}>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('topography')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.topography}>
                  <Select
                    value={formData.topography || ""}
                    onChange={handleInputChange('topography')}
                    displayEmpty
                    error={!formData.topography}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {topographies?.map((topography) => (
                      <MenuItem key={topography.id} value={topography.id}>
                        {currentLanguage === 'ar' ? topography.name_ar : topography.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.topography && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('topographyRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('locationStatus')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.location_status}>
                  <Select
                    value={formData.location_status || ""}
                    onChange={handleInputChange('location_status')}
                    displayEmpty
                    error={!formData.location_status}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {locationStatuses?.map((locationStatus) => (
                      <MenuItem key={locationStatus.id} value={locationStatus.id}>
                        {currentLanguage === 'ar' ? locationStatus.name_ar : locationStatus.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.location_status && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('locationStatusRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('soilTexture')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.soil_texture}>
                  <Select
                    value={formData.soil_texture || ""}
                    onChange={handleInputChange('soil_texture')}
                    displayEmpty
                    error={!formData.soil_texture}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {soilTextures?.map((soilTexture) => (
                      <MenuItem key={soilTexture.id} value={soilTexture.id}>
                        {currentLanguage === 'ar' ? soilTexture.name_ar : soilTexture.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.soil_texture && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('soilTextureRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('soilColor')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.soil_color}>
                  <Select
                    value={formData.soil_color || ""}
                    onChange={handleInputChange('soil_color')}
                    displayEmpty
                    error={!formData.soil_color}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {soilColors?.map((soilColor) => (
                      <MenuItem key={soilColor.id} value={soilColor.id}>
                        {currentLanguage === 'ar' ? soilColor.name_ar : soilColor.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.soil_color && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('soilColorRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('hardness')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.hardness}>
                  <Select
                    value={formData.hardness || ""}
                    onChange={handleInputChange('hardness')}
                    displayEmpty
                    error={!formData.hardness}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {hardnesses?.map((hardness) => (
                      <MenuItem key={hardness.id} value={hardness.id}>
                        {currentLanguage === 'ar' ? hardness.name_ar : hardness.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.hardness && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('hardnessRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  fontWeight: 500, 
                  fontSize: '18px',
                  color: '#374151',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('drainage')}
                </Typography>
                <FormControl fullWidth sx={getFieldStyles()} required error={!formData.drainage}>
                  <Select
                    value={formData.drainage || ""}
                    onChange={handleInputChange('drainage')}
                    displayEmpty
                    error={!formData.drainage}
                  >
                    <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                    {drainages?.map((drainage) => (
                      <MenuItem key={drainage.id} value={drainage.id}>
                        {currentLanguage === 'ar' ? drainage.name_ar : drainage.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.drainage && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                      {t('drainageRequired')}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Diseases, pH and Additional Settings */}
            <Paper sx={{ 
              p: 3, 
              mt: 4,
              bgcolor: '#f8fafc', 
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Typography variant="subtitle1" sx={{ 
                mb: 3, 
                color: '#1e293b', 
                fontWeight: 600
              }}>
                🔍 {t('additionalSettings')}
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 3
              }}>
                <Box>
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    fontSize: '18px',
                    color: '#374151',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('topDiseasesPests')}
                  </Typography>
                  <FormControl fullWidth sx={getFieldStyles()}>
                    <Select
                      multiple
                      value={formData.diseases_pests || []}
                      onChange={handleInputChange('diseases_pests')}
                      displayEmpty
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as number[]).map((value) => {
                            const selectedPest = diseasePests.find(p => p.id === value);
                            return <Chip key={value} label={currentLanguage === 'ar' ? selectedPest?.name_ar : selectedPest?.name_en} />;
                          })}
                        </Box>
                      )}
                    >
                      {diseasePests?.map((diseasePest) => (
                        <MenuItem key={diseasePest.id} value={diseasePest.id}>
                          <Checkbox checked={(formData.diseases_pests || []).indexOf(diseasePest.id) > -1} />
                          <ListItemText primary={currentLanguage === 'ar' ? diseasePest.name_ar : diseasePest.name_en} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    fontSize: '18px',
                    color: '#374151',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('soilPH')}
                  </Typography>
                  <FormControl fullWidth sx={getFieldStyles()}>
                    <Select
                      value={formData.soil_ph || ""}
                      onChange={handleInputChange('soil_ph')}
                      displayEmpty
                    >
                      <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                      {soilPHs?.map((soilPH) => (
                        <MenuItem key={soilPH.id} value={soilPH.id}>
                          {currentLanguage === 'ar' 
                            ? `${soilPH.name_ar} - ${soilPH.min_value}-${soilPH.max_value}` 
                            : `${soilPH.name_en} - ${soilPH.min_value}-${soilPH.max_value}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    fontSize: '18px',
                    color: '#374151',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('waterPH')}
                  </Typography>
                  <FormControl fullWidth sx={getFieldStyles()}>
                    <Select
                      value={formData.water_ph || ""}
                      onChange={handleInputChange('water_ph')}
                      displayEmpty
                    >
                      <MenuItem value="">{t('pleaseSelect')}</MenuItem>
                      {waterPHs?.map((waterPH) => (
                        <MenuItem key={waterPH.id} value={waterPH.id}>
                          {currentLanguage === 'ar' 
                            ? `${waterPH.name_ar} - ${waterPH.min_value}-${waterPH.max_value}` 
                            : `${waterPH.name_en} - ${waterPH.min_value}-${waterPH.max_value}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Switches Section */}
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_local_seed}
                      onChange={handleInputChange('is_local_seed')}
                    />
                  }
                  label={t('seedOriginLocal')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_herbarium_sample}
                      onChange={handleInputChange('is_herbarium_sample')}
                    />
                  }
                  label={t('isHerbariumSample')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.has_photos}
                      onChange={handleInputChange('has_photos')}
                    />
                  }
                  label={t('hasPhotos')}
                />
              </Box>

              {/* Photos Upload Section */}
              {formData.has_photos && (
                <Box mt={2} p={2} border={1} borderColor="grey.300" borderRadius={1}>
                  <Typography variant="subtitle1" gutterBottom>{t('uploadPhotos')}</Typography>
                  
                  {/* عرض الصور الموجودة */}
                  {existingPhotos.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        {t('existingPhotos')} ({existingPhotos.length})
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        {existingPhotos.map((photo, index) => (
                          <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                            <img 
                              src={photo.photo} 
                              alt={photo.description || `Photo ${index + 1}`} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} 
                            />
                            <Box sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                              color: 'white',
                              fontSize: '0.7rem',
                              padding: '2px 4px',
                              borderBottomLeftRadius: '4px',
                              borderBottomRightRadius: '4px'
                            }}>
                              {t('existing')}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  {/* إضافة صور جديدة */}
                  <Typography variant="subtitle2" gutterBottom>
                    {t('addNewPhotos')}
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<AddPhotoAlternateIcon />}
                  >
                    {t('selectPhotos')}
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={(e) => setPhotos(prev => [...prev, ...Array.from(e.target.files || [])])}
                      accept="image/*"
                    />
                  </Button>
                  <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                    {photos.map((file, index) => (
                      <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={file.name} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} 
                        />
                        <IconButton
                          size="small"
                          onClick={() => setPhotos(photos.filter((_, i) => i !== index))}
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        );

      case 3: // Additional Info
        return (
          <Box>
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              ℹ️ {t('additionalInfo')}
            </Typography>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '18px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('notes')}
              </Typography>
              <TextField
                fullWidth
                value={formData.notes || ''}
                onChange={handleInputChange('notes')}
                multiline
                rows={6}
                sx={getFieldStyles()}
                placeholder={t('notesPlaceholder')}
                inputProps={{
                  dir: isRTL ? 'rtl' : 'ltr',
                  style: {
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }
                }}
              />
            </Box>
          </Box>
        );

      case 4: // Review & Submit
        return (
          <Box>
            <Typography variant="h6" sx={{ 
              mb: 3, 
              color: '#1e293b', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              ✅ {t('reviewAndSubmit')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('reviewYourData')}
            </Typography>
            {/* You can add a summary of the data here if needed */}
          </Box>
        );

      default:
        return null;
    }
  };

  // Show loading state when fetching data
  if (loading || dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        px: { xs: 1, sm: 2, md: 3 }, // تباعد متجاوب للجانبين
        py: { xs: 2, sm: 3 } // تباعد متجاوب للأعلى والأسفل
      }}>
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 2, sm: 3, md: 4 },
        p: { xs: 2, sm: 3, md: 4 }, // تباعد متجاوب للداخل
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: { xs: 2, md: 3 }, // زوايا متجاوبة
        color: 'white'
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            mb: 1,
            textAlign: i18n.language === 'ar' ? 'right' : 'left',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } // حجم خط متجاوب
          }}
        >
          {isEdit ? t('editSurvey') : t('createSurvey')}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            opacity: 0.9,
            textAlign: i18n.language === 'ar' ? 'right' : 'left',
            fontSize: { xs: '0.875rem', sm: '1rem' } // حجم خط متجاوب
          }}
        >
          {t('fillSurveyForm')}
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            textAlign: i18n.language === 'ar' ? 'right' : 'left',
            borderRadius: 2,
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3, 
            textAlign: i18n.language === 'ar' ? 'right' : 'left',
            borderRadius: 2,
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}
        >
          {success}
        </Alert>
      )}

      {/* Progress Stepper */}
      <Paper sx={{ 
        p: { xs: 2, sm: 3, md: 4 }, // تباعد متجاوب
        mb: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: 2, md: 3 },
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e2e8f0',
        direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
      }}>
        <Typography variant="h6" sx={{ 
          mb: { xs: 2, sm: 3 }, 
          color: '#1e293b', 
          fontWeight: 600,
          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
          textAlign: i18n.language === 'ar' ? 'right' : { xs: 'center', sm: 'left' },
          direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
        }}>
          {t('progress')}: {activeStep + 1} / {steps.length}
        </Typography>

        {/* Custom Progress Stepper */}
        <Box sx={{ 
          width: '100%', 
          direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
          mb: 2
        }}>
          {/* Progress Bar */}
          <Box sx={{ 
            position: 'relative', 
            height: '4px', 
            backgroundColor: '#e2e8f0',
            borderRadius: '2px',
            mb: 3,
            mx: { xs: 2, sm: 3 }
          }}>
            <Box sx={{ 
              position: 'absolute',
              top: 0,
              left: i18n.language === 'ar' ? 'auto' : 0,
              right: i18n.language === 'ar' ? 0 : 'auto',
              height: '100%',
              width: `${((activeStep + 1) / steps.length) * 100}%`,
              backgroundColor: '#667eea',
              borderRadius: '2px',
              transition: 'width 0.3s ease-in-out'
            }} />
          </Box>

          {/* Desktop/Tablet Steps */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' },
            flexDirection: i18n.language === 'ar' ? 'row-reverse' : 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1
          }}>
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              
              return (
                <Box 
                  key={index}
                  sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    px: 1
                  }}
                >
                  {/* Step Circle */}
                  <Box sx={{ 
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 600,
                    mb: 1,
                    backgroundColor: isCompleted ? '#10b981' : isActive ? '#667eea' : '#e2e8f0',
                    color: isCompleted || isActive ? 'white' : '#64748b',
                    transition: 'all 0.3s ease-in-out',
                    border: isActive ? '3px solid #667eea' : '3px solid transparent',
                    boxShadow: isActive ? '0 0 0 4px rgba(102, 126, 234, 0.1)' : 'none'
                  }}>
                    {isCompleted ? (
                      <Box component="span" sx={{ fontSize: '1rem' }}>✓</Box>
                    ) : (
                      index + 1
                    )}
                  </Box>

                  {/* Step Label */}
                  <Typography sx={{ 
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#667eea' : isCompleted ? '#10b981' : '#64748b',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    transition: 'color 0.3s ease-in-out',
                    wordBreak: 'break-word',
                    hyphens: 'auto'
                  }}>
                    {step.title}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Mobile Steps (Simplified) */}
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1
          }}>
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              
              return (
                <Box 
                  key={index}
                  sx={{ 
                    width: isActive ? 12 : 8,
                    height: isActive ? 12 : 8,
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? '#10b981' : isActive ? '#667eea' : '#cbd5e0',
                    transition: 'all 0.3s ease-in-out',
                    border: isActive ? '2px solid #667eea' : 'none',
                    boxShadow: isActive ? '0 0 0 3px rgba(102, 126, 234, 0.2)' : 'none'
                  }}
                />
              );
            })}
          </Box>

          {/* Mobile Current Step Label */}
          <Typography sx={{ 
            display: { xs: 'block', sm: 'none' },
            textAlign: 'center',
            mt: 2,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#667eea'
          }}>
            {steps[activeStep].title}
          </Typography>
        </Box>
      </Paper>

      {/* Form Card */}
      <Paper sx={{ 
        borderRadius: { xs: 2, md: 3 },
        boxShadow: { xs: '0 2px 8px rgba(0, 0, 0, 0.1)', md: '0 10px 25px rgba(0, 0, 0, 0.1)' },
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        mb: { xs: 2, sm: 3 }
      }}>
        {/* Form Header */}
        <Box sx={{
          p: { xs: 2, sm: 3, md: 4 },
          background: 'linear-gradient(90deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <Typography variant="h6" sx={{ 
            color: '#1e293b', 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
          }}>
            <Box sx={{
              width: { xs: 6, sm: 8 },
              height: { xs: 24, sm: 32 },
              borderRadius: 1,
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
            }} />
            {steps[activeStep].title}
          </Typography>
        </Box>

        {/* Form Content */}
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box component="form" onSubmit={handleSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: { xs: 4, sm: 5, md: 6 },
              pt: { xs: 3, sm: 4 },
              borderTop: '1px solid #e2e8f0',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  borderColor: '#d1d5db',
                  color: '#6b7280',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  width: { xs: '100%', sm: 'auto' },
                  order: { xs: 2, sm: 1 },
                  '&:hover': {
                    borderColor: '#9ca3af',
                    bgcolor: '#f9fafb'
                  }
                }}
              >
                {t('previous')}
              </Button>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: '#6b7280',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                order: { xs: 1, sm: 2 }
              }}>
                {activeStep + 1} / {steps.length}
              </Box>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !isCurrentStepValid()}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{
                    borderRadius: 2,
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    background: (!loading && isCurrentStepValid()) 
                      ? 'linear-gradient(45deg, #667eea, #764ba2)'
                      : '#9ca3af',
                    boxShadow: (!loading && isCurrentStepValid()) 
                      ? '0 4px 14px 0 rgba(102, 126, 234, 0.4)'
                      : 'none',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    width: { xs: '100%', sm: 'auto' },
                    order: { xs: 3, sm: 3 },
                    '&:hover': (!loading && isCurrentStepValid()) ? {
                      background: 'linear-gradient(45deg, #5a67d8, #6b46c1)',
                      boxShadow: '0 6px 20px 0 rgba(102, 126, 234, 0.6)',
                    } : {},
                    '&.Mui-disabled': {
                      background: '#9ca3af',
                      color: '#ffffff',
                      opacity: 0.6
                    }
                  }}
                >
                  {loading ? t('loading') : t('finish')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid()}
                  sx={{
                    borderRadius: 2,
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    background: isCurrentStepValid() 
                      ? 'linear-gradient(45deg, #667eea, #764ba2)'
                      : '#9ca3af',
                    boxShadow: isCurrentStepValid() 
                      ? '0 4px 14px 0 rgba(102, 126, 234, 0.4)'
                      : 'none',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    width: { xs: '100%', sm: 'auto' },
                    order: { xs: 3, sm: 3 },
                    '&:hover': isCurrentStepValid() ? {
                      background: 'linear-gradient(45deg, #5a67d8, #6b46c1)',
                      boxShadow: '0 6px 20px 0 rgba(102, 126, 234, 0.6)',
                    } : {},
                    '&.Mui-disabled': {
                      background: '#9ca3af',
                      color: '#ffffff',
                      opacity: 0.6
                    }
                  }}
                >
                  {t('next')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Participant Add/Edit Dialog */}
      <Dialog
        open={participantDialog.open}
        onClose={closeParticipantDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <PersonIcon sx={{ color: '#667eea' }} />
          {participantDialog.editIndex >= 0 ? t('editParticipant') : t('addParticipant')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '16px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('civilId')}
              </Typography>
              <TextField
                fullWidth
                value={participantDialog.participant.civilid}
                onChange={async (e) => {
                  const civilid = e.target.value;
                  handleParticipantInputChange('civilid')(e);
                  
                  // البحث التلقائي عند إدخال الرقم المدني
                  if (civilid.length >= 8) {
                    try {
                      const existingParticipant = await apiService.getParticipantByCivilId(civilid);
                      if (existingParticipant) {
                        setParticipantDialog(prev => ({
                          ...prev,
                          participant: {
                            ...prev.participant,
                            civilid: existingParticipant.civilid || '',
                            name: existingParticipant.name || '',
                            phone: existingParticipant.phone || '',
                            department: existingParticipant.department
                          }
                        }));
                        setSnackbar({
                          open: true,
                          message: t('participantFoundAndLoaded'),
                          severity: 'success'
                        });
                      }
                    } catch (error) {
                      console.error('Error searching participant:', error);
                    }
                  }
                }}
                variant="outlined"
                required
                placeholder={t('civilId')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#fff'
                  }
                }}
                inputProps={{
                  dir: 'ltr',
                  style: {
                    textAlign: 'left',
                    direction: 'ltr'
                  }
                }}
              />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '16px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('participantName')}
              </Typography>
              <TextField
                fullWidth
                value={participantDialog.participant.name}
                onChange={handleParticipantInputChange('name')}
                variant="outlined"
                required
                placeholder={t('participantName')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#fff'
                  }
                }}
                inputProps={{
                  dir: isRTL ? 'rtl' : 'ltr',
                  style: {
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }
                }}
              />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '16px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('phone')}
              </Typography>
              <TextField
                fullWidth
                value={participantDialog.participant.phone}
                onChange={handleParticipantInputChange('phone')}
                variant="outlined"
                required
                placeholder={t('phone')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#fff'
                  }
                }}
                inputProps={{
                  dir: isRTL ? 'rtl' : 'ltr',
                  style: {
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }
                }}
              />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '16px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('department')}
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="department-select-label">{t('selectDepartment')}</InputLabel>
                <Select
                  labelId="department-select-label"
                  value={participantDialog.participant.department || ''}
                  onChange={(event) => {
                    const value = Number(event.target.value) || undefined;
                    setParticipantDialog(prev => ({
                      ...prev,
                      participant: {
                        ...prev.participant,
                        department: value
                      }
                    }));
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {i18n.language === 'ar' ? dept.name_ar : dept.name_en}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={closeParticipantDialog}
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              color: '#64748b',
              borderColor: '#cbd5e1',
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#f8fafc'
              }
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={saveParticipant}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              background: 'linear-gradient(45deg, #4f46e5, #6366f1)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a67d8, #6b46c1)',
              }
            }}
          >
            {participantDialog.editIndex >= 0 ? t('update') : t('add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Participant Dialog - تم إزالة الحوار المكرر */}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={closeDeleteConfirmDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#dc2626'
        }}>
          <DeleteIcon />
          {t('deleteParticipant')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            {t('confirmDeleteParticipant')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={closeDeleteConfirmDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={deleteParticipant}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Farmer Dialog */}
      <Dialog 
        open={farmerDialog.open} 
        onClose={closeFarmerDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#f8fafc', 
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '20px',
          fontWeight: 600,
          color: '#1e293b'
        }}>
          👨‍🌾 {t('addNewFarmer')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '16px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('farmerName')} *
              </Typography>
              <TextField
                fullWidth
                value={farmerDialog.farmer.name}
                onChange={handleFarmerInputChange('name')}
                sx={getFieldStyles()}
                placeholder={t('farmerName')}
                inputProps={{
                  dir: isRTL ? 'rtl' : 'ltr',
                  style: {
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }
                }}
              />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '16px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('civilId')} *
              </Typography>
              <TextField
                fullWidth
                value={farmerDialog.farmer.civilid}
                onChange={handleFarmerInputChange('civilid')}
                sx={getFieldStyles()}
                placeholder={t('civilId')}
                inputProps={{
                  dir: isRTL ? 'rtl' : 'ltr',
                  style: {
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }
                }}
              />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '16px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('phone')}
              </Typography>
              <TextField
                fullWidth
                value={farmerDialog.farmer.phone}
                onChange={handleFarmerInputChange('phone')}
                sx={getFieldStyles()}
                placeholder={t('phone')}
                inputProps={{
                  dir: isRTL ? 'rtl' : 'ltr',
                  style: {
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }
                }}
              />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                fontSize: '16px',
                color: '#374151',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('notes')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={farmerDialog.farmer.notes}
                onChange={handleFarmerInputChange('notes')}
                sx={getFieldStyles()}
                placeholder={t('notes')}
                inputProps={{
                  dir: isRTL ? 'rtl' : 'ltr',
                  style: {
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={closeFarmerDialog}
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              color: '#64748b',
              borderColor: '#cbd5e1',
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#f8fafc'
              }
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={saveFarmer}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              bgcolor: '#10b981',
              '&:hover': {
                bgcolor: '#059669'
              }
            }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
};

export default SurveyForm;
